import { useState, useEffect } from "react";
import API from "../lib/api";
import { Clock, BookOpen, Gamepad2, Moon, Loader2, Check } from "lucide-react";
import { Slider } from "../components/ui/slider";

const DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
const DAY_LABELS = { monday: "Mon", tuesday: "Tue", wednesday: "Wed", thursday: "Thu", friday: "Fri", saturday: "Sat", sunday: "Sun" };

const MODES = [
  {
    id: "school",
    label: "School Mode",
    desc: "Limited apps, no games",
    icon: BookOpen,
    color: "#4D96FF",
    bg: "bg-[#4D96FF]/10",
    border: "border-[#4D96FF]",
  },
  {
    id: "free_time",
    label: "Free Time",
    desc: "All approved apps available",
    icon: Gamepad2,
    color: "#FFD23F",
    bg: "bg-[#FFD23F]/10",
    border: "border-[#FFD23F]",
  },
  {
    id: "bedtime",
    label: "Bedtime Mode",
    desc: "Device locked, time to rest",
    icon: Moon,
    color: "#6366F1",
    bg: "bg-[#6366F1]/10",
    border: "border-[#6366F1]",
  },
];

export default function ScreenTimePage() {
  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modeLoading, setModeLoading] = useState("");
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    fetchPolicy();
  }, []);

  const fetchPolicy = async () => {
    try {
      const { data } = await API.get("/screen-time");
      setPolicy(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const savePolicy = async () => {
    setSaving(true);
    setSaved(false);
    try {
      await API.put("/screen-time", {
        daily_limit_minutes: policy.daily_limit_minutes,
        schedule: policy.schedule,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const setMode = async (modeId) => {
    setModeLoading(modeId);
    try {
      const { data } = await API.put("/screen-time/mode", { mode: modeId });
      setPolicy((prev) => ({ ...prev, mode: data.mode, mode_label: data.mode_label, daily_limit_minutes: data.daily_limit_minutes }));
    } catch (err) {
      console.error(err);
    } finally {
      setModeLoading("");
    }
  };

  const updateDayLimit = (day, value) => {
    setPolicy((prev) => ({
      ...prev,
      schedule: { ...prev.schedule, [day]: value },
    }));
  };

  const formatTime = (mins) => {
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    if (h === 0) return `${m}m`;
    if (m === 0) return `${h}h`;
    return `${h}h ${m}m`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 text-[#FF5C39] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="screen-time-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-extrabold text-[#1F1F2E] tracking-tight">Screen Time</h1>
        <p className="text-sm font-semibold text-[#52526B] mt-1">Manage how much time your child spends on their device</p>
      </div>

      {/* Current usage */}
      <div className="bg-white rounded-[32px] p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-[#F0E4D8]/50" data-testid="current-usage-card">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-[#FF5C39]/10 rounded-xl flex items-center justify-center">
            <Clock className="w-5 h-5 text-[#FF5C39]" strokeWidth={2.5} />
          </div>
          <div>
            <h3 className="font-bold text-[#1F1F2E]">Today's usage</h3>
            <p className="text-xs font-semibold text-[#52526B]">{policy?.mode_label || "Free Time"} active</p>
          </div>
        </div>
        <div className="bg-[#FFF0E0] rounded-2xl p-4">
          <div className="flex justify-between mb-2">
            <span className="text-sm font-bold text-[#1F1F2E]">{formatTime(policy?.used_today_minutes || 0)} used</span>
            <span className="text-sm font-bold text-[#52526B]">{formatTime(policy?.daily_limit_minutes || 120)} limit</span>
          </div>
          <div className="w-full h-3 bg-[#F0E4D8] rounded-full overflow-hidden">
            <div
              className="h-full bg-[#FF5C39] rounded-full transition-all"
              style={{ width: `${Math.min(100, ((policy?.used_today_minutes || 0) / (policy?.daily_limit_minutes || 120)) * 100)}%` }}
            />
          </div>
        </div>
      </div>

      {/* Preset Modes */}
      <div>
        <h3 className="text-sm font-bold uppercase tracking-wider text-[#52526B] mb-3">Quick modes</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {MODES.map((mode) => {
            const Icon = mode.icon;
            const isActive = policy?.mode === mode.id;
            return (
              <button
                key={mode.id}
                onClick={() => setMode(mode.id)}
                disabled={!!modeLoading}
                data-testid={`mode-${mode.id}`}
                className={`relative p-5 rounded-2xl border-2 text-left transition-all active:scale-95 ${
                  isActive ? `${mode.bg} ${mode.border}` : "bg-white border-[#F0E4D8] hover:border-[#52526B]/30"
                }`}
              >
                {modeLoading === mode.id ? (
                  <Loader2 className="w-8 h-8 animate-spin" style={{ color: mode.color }} />
                ) : (
                  <Icon className="w-8 h-8 mb-3" style={{ color: mode.color }} strokeWidth={2.5} />
                )}
                <p className="font-extrabold text-[#1F1F2E] text-sm">{mode.label}</p>
                <p className="text-xs font-semibold text-[#52526B] mt-0.5">{mode.desc}</p>
                {isActive && (
                  <div className="absolute top-3 right-3 w-6 h-6 rounded-full flex items-center justify-center" style={{ backgroundColor: mode.color }}>
                    <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Daily Limit */}
      <div className="bg-white rounded-[32px] p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-[#F0E4D8]/50" data-testid="daily-limit-card">
        <h3 className="font-bold text-[#1F1F2E] mb-4">Daily time limit</h3>
        <div className="mb-4">
          <p className="text-3xl font-extrabold text-[#FF5C39] mb-4" data-testid="daily-limit-value">
            {formatTime(policy?.daily_limit_minutes || 120)}
          </p>
          <Slider
            value={[policy?.daily_limit_minutes || 120]}
            onValueChange={([v]) => setPolicy({ ...policy, daily_limit_minutes: v })}
            min={0}
            max={480}
            step={15}
            data-testid="daily-limit-slider"
            className="[&_[role=slider]]:bg-[#FF5C39] [&_[role=slider]]:border-0 [&_[role=slider]]:w-6 [&_[role=slider]]:h-6 [&_.range]:bg-[#FF5C39]"
          />
          <div className="flex justify-between mt-2 text-xs font-bold text-[#52526B]">
            <span>0h</span>
            <span>2h</span>
            <span>4h</span>
            <span>6h</span>
            <span>8h</span>
          </div>
        </div>
      </div>

      {/* Weekly Schedule */}
      <div className="bg-white rounded-[32px] p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-[#F0E4D8]/50" data-testid="weekly-schedule-card">
        <h3 className="font-bold text-[#1F1F2E] mb-4">Weekly schedule</h3>
        <div className="space-y-4">
          {DAYS.map((day) => {
            const limit = policy?.schedule?.[day] ?? 120;
            const isWeekend = day === "saturday" || day === "sunday";
            return (
              <div key={day} className="flex items-center gap-4" data-testid={`schedule-${day}`}>
                <span className={`w-10 text-sm font-bold ${isWeekend ? "text-[#FF5C39]" : "text-[#1F1F2E]"}`}>
                  {DAY_LABELS[day]}
                </span>
                <div className="flex-1">
                  <Slider
                    value={[limit]}
                    onValueChange={([v]) => updateDayLimit(day, v)}
                    min={0}
                    max={480}
                    step={15}
                    className={`[&_[role=slider]]:border-0 [&_[role=slider]]:w-5 [&_[role=slider]]:h-5 ${
                      isWeekend
                        ? "[&_[role=slider]]:bg-[#FF5C39] [&_.range]:bg-[#FF5C39]"
                        : "[&_[role=slider]]:bg-[#4D96FF] [&_.range]:bg-[#4D96FF]"
                    }`}
                  />
                </div>
                <span className="w-14 text-right text-sm font-bold text-[#52526B]">{formatTime(limit)}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Save button */}
      <div className="sticky bottom-20 md:bottom-4 z-30">
        <button
          onClick={savePolicy}
          disabled={saving}
          data-testid="save-screen-time-button"
          className={`w-full font-bold rounded-2xl px-6 py-4 active:scale-95 transition-all shadow-[0_8px_20px_rgba(255,92,57,0.25)] flex items-center justify-center gap-2 ${
            saved ? "bg-[#34D399] text-white" : "bg-[#FF5C39] text-white hover:bg-[#E04828]"
          }`}
        >
          {saving ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : saved ? (
            <>
              <Check className="w-5 h-5" strokeWidth={3} />
              Saved!
            </>
          ) : (
            "Save changes"
          )}
        </button>
      </div>
    </div>
  );
}
